import { STANDARD_PREMIUM_PRICE_IDS } from 'Containers/Settings/containers/Payments/utils/subscriptionUtils';

const hexToRgba = (hex, opacity) => {
  const normalized = hex.replace('#', '');
  const r = parseInt(normalized.substring(0, 2), 16);
  const g = parseInt(normalized.substring(2, 4), 16);
  const b = parseInt(normalized.substring(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

const PLAN_THEMES = {
  pro: {
    id: 'pro',
    icon: 'rocket',
    theme: {
      accent: '#6B70F5',
      accentSoft: '#EEF0FF',
      accentBorder: 'rgba(107, 112, 245, 0.28)',
    },
  },
  premium: {
    id: 'premium',
    icon: 'flame',
    theme: {
      accent: '#FF8A3D',
      accentSoft: '#FFF4E6',
      accentBorder: 'rgba(255, 138, 61, 0.28)',
    },
  },
  base: {
    id: 'base',
    icon: 'layers',
    theme: {
      accent: '#858AF9',
      accentSoft: '#DFE0FF',
      accentBorder: 'rgba(133, 138, 249, 0.28)',
    },
  },
};

/** Soglie legacy sul campo DB `prezzo` (bucket catalogo mensile). */
export const resolvePlanIdFromPriceAmount = (price) => {
  const normalizedPrice = Number(price) || 0;
  if (normalizedPrice >= 75) return 'pro';
  if (normalizedPrice >= 50) return 'premium';
  return 'base';
};

/**
 * Come il BE costruisce `prezziPremium` (getDaysLeft / getPremiumPriceIds):
 * - prezzo < 31 (Basic): lista price ID Stripe di upgrade → Premium
 * - prezzo >= 31 (Premium/Pro nativi): array VUOTO
 *
 * Quindi `currentPriceId ∈ prezziPremium` vale solo per Basic che hanno fatto upgrade.
 * I Premium/Pro nativi si riconoscono dal bucket DB `prezzo` (>=50 / >=75).
 * In più: se lo status punta a un price del listino Premium standard → Premium.
 */
export const resolvePlanIdFromSubscription = ({
  products = {},
  currentPriceId = null,
} = {}) => {
  const premiumIds = (products?.prezziPremium || []).map(String);
  const activePriceId = currentPriceId != null ? String(currentPriceId) : null;

  // Basic → Premium upgrade: lo status Stripe punta a un price ID nella lista upgrade
  if (activePriceId && premiumIds.length && premiumIds.includes(activePriceId)) {
    return 'premium';
  }

  // Listino commerciale Premium (anche se prezziPremium BE è vuoto/incompleto)
  if (activePriceId && STANDARD_PREMIUM_PRICE_IDS.has(activePriceId)) {
    return 'premium';
  }

  // Premium/Pro nativi (o status non ancora pronto): bucket catalogo DB
  return resolvePlanIdFromPriceAmount(products?.prezzo);
};

export const getActivePlan = (priceOrContext) => {
  if (
    priceOrContext &&
    typeof priceOrContext === 'object' &&
    ('products' in priceOrContext || 'currentPriceId' in priceOrContext)
  ) {
    const planId = resolvePlanIdFromSubscription(priceOrContext);
    return PLAN_THEMES[planId] || PLAN_THEMES.base;
  }

  if (typeof priceOrContext === 'number' || typeof priceOrContext === 'string') {
    const planId = resolvePlanIdFromPriceAmount(priceOrContext);
    return PLAN_THEMES[planId] || PLAN_THEMES.base;
  }

  return PLAN_THEMES.base;
};

/** Premium + Pro sbloccati; Base locked. Accetta prezzo, plan id, oggetto piano o contesto products. */
export const isPremiumPlan = (priceOrPlan) => {
  if (typeof priceOrPlan === 'object' && priceOrPlan?.id) {
    return priceOrPlan.id === 'premium' || priceOrPlan.id === 'pro';
  }
  if (typeof priceOrPlan === 'string') {
    return priceOrPlan === 'premium' || priceOrPlan === 'pro';
  }
  if (
    priceOrPlan &&
    typeof priceOrPlan === 'object' &&
    ('products' in priceOrPlan || 'currentPriceId' in priceOrPlan)
  ) {
    const planId = resolvePlanIdFromSubscription(priceOrPlan);
    return planId === 'premium' || planId === 'pro';
  }
  return getActivePlan(priceOrPlan).id === 'premium' || getActivePlan(priceOrPlan).id === 'pro';
};

export const getPlanGhostBackground = (hex, opacity = 0.7) => hexToRgba(hex, opacity);

export const getPlanLabels = (planId, t) => ({
  label: t(`header.plan.plans.${planId}.label`),
  shortLabel: t(`header.plan.plans.${planId}.shortLabel`),
});

export const getPlanModalMessage = (planId, t) => {
  if (planId === 'base') {
    return {
      type: 'upgrade',
      eyebrow: t('header.plan.messages.base.eyebrow'),
      title: t('header.plan.messages.base.title'),
      description: t('header.plan.messages.base.description'),
      highlights: t('header.plan.messages.base.highlights', { returnObjects: true }),
      cta: t('header.plan.messages.base.cta'),
      secondaryCta: t('header.plan.messages.base.secondaryCta'),
    };
  }

  if (planId === 'premium') {
    return {
      type: 'active',
      title: t('header.plan.messages.premium.title'),
      description: t('header.plan.messages.premium.description'),
      linkCta: t('header.plan.messages.premium.linkCta'),
    };
  }

  return {
    type: 'active',
    title: t('header.plan.messages.pro.title'),
    description: t('header.plan.messages.pro.description'),
    linkCta: t('header.plan.messages.pro.linkCta'),
  };
};
